import { useEffect, useState } from "react";
import { Check, Clock, AlertCircle, CalendarClock, CalendarX2, UserX } from "lucide-react";
import api from "@/lib/api";

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" }) +
      " · " + d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const daysSince = (iso) => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.floor(ms / 86400000);
};

// Presentation for each `teams_event_changes.kind` value emitted by the
// Python polling service. Icons come from lucide-react; colours follow
// the semantic tokens (amber = warning/reschedule, red = cancellation).
const TEAMS_CHANGE_META = {
  rescheduled: {
    label: "Reunião reagendada",
    icon: CalendarClock,
    iconBg: "bg-[hsl(var(--warning-bg))]",
    iconColor: "text-[hsl(var(--warning))]",
    titleColor: "text-[hsl(var(--warning))]",
    borderL: "border-l-amber-500",
  },
  cancelled_by_organizer: {
    label: "Reunião cancelada (organizador via Outlook)",
    icon: CalendarX2,
    iconBg: "bg-[hsl(var(--danger-bg))]",
    iconColor: "text-[hsl(var(--danger))]",
    titleColor: "text-[hsl(var(--danger))]",
    borderL: "border-l-red-500",
  },
  cancelled_by_client: {
    label: "Cliente recusou a reunião",
    icon: UserX,
    iconBg: "bg-[hsl(var(--danger-bg))]",
    iconColor: "text-[hsl(var(--danger))]",
    titleColor: "text-[hsl(var(--danger))]",
    borderL: "border-l-red-500",
  },
  cancelled_deleted: {
    label: "Evento eliminado do calendário",
    icon: CalendarX2,
    iconBg: "bg-[hsl(var(--danger-bg))]",
    iconColor: "text-[hsl(var(--danger))]",
    titleColor: "text-[hsl(var(--danger))]",
    borderL: "border-l-red-500",
  },
};

export default function LeadTimeline({ lead }) {
  const [changes, setChanges] = useState([]);
  useEffect(() => {
    if (!lead?.id) return;
    let cancelled = false;
    api.get(`/leads/${lead.id}/teams-changes`)
      .then(r => { if (!cancelled) setChanges(r.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lead?.id]);

  if (!lead) return null;
  const est = lead.estimate || {};
  const prop = lead.proposal || {};
  const supersededVersions = Array.isArray(prop.versions)
    ? prop.versions.filter((v) => v.replaced_at)
    : [];

  const phases = [
    {
      key: "created",
      label: "Lead criado",
      at: lead.created_at,
      by: lead.created_by_name || null,
      done: true,
    },
    {
      key: "estimate_sent",
      label: "Estimativa enviada",
      at: est.qt_snapshot_before?.captured_at || (
        ["estimativa_enviada","qt_agendada","qt_realizada","proposta_enviada","aceite","recusada"].includes(lead.status) ? est.updated_at : null
      ),
      done: ["estimativa_enviada","qt_agendada","qt_realizada","proposta_enviada","aceite","recusada"].includes(lead.status),
    },
    {
      key: "qt_scheduled",
      label: "QT agendada",
      at: lead.scheduled_qt_at,
      by: lead.assigned_manager_name || null,
      done: !!lead.scheduled_qt_at,
    },
    {
      key: "qt_done",
      label: est.qt_adjusted ? "QT (com ajuste)" : "QT confirmada",
      at: est.qt_confirmed_at,
      by: est.qt_confirmed_by_name || null,
      reason: est.qt_adjusted ? est.qt_adjustment_reason : null,
      done: !!est.qt_confirmed_at,
    },
    {
      key: "proposal",
      label: "Proposta enviada",
      at: prop.generated_at,
      done: ["proposta_enviada","aceite","recusada","proposta_perdida","negocio_ganho","negocio_perdido"].includes(lead.status),
      subtitle: prop.current_version ? `Versão ${prop.current_version}` : null,
    },
    {
      key: "outcome",
      // 25/02/2026 — Labels alinhados com o novo funil (Proposta Aceite,
      // Proposta Recusada, Proposta Perdida, Negócio Ganho/Perdido).
      label: (
        lead.status === "aceite" ? "Proposta Aceite" :
        lead.status === "negocio_ganho" ? "Negócio Ganho" :
        lead.status === "recusada" ? "Proposta Recusada" :
        lead.status === "proposta_perdida" ? "Proposta Perdida" :
        (lead.status === "perdida" || lead.status === "negocio_perdido") ? "Negócio Perdido" :
        "Aguarda resposta"
      ),
      at: (lead.signature || {}).signed_at || (["aceite","recusada","perdida","proposta_perdida","negocio_ganho","negocio_perdido"].includes(lead.status) ? lead.updated_at : null),
      done: ["aceite","recusada","perdida","proposta_perdida","negocio_ganho","negocio_perdido"].includes(lead.status),
      terminal: true,
      variant: (
        (lead.status === "aceite" || lead.status === "negocio_ganho") ? "success" :
        (["recusada","perdida","proposta_perdida","negocio_perdido"].includes(lead.status)) ? "danger" :
        "pending"
      ),
    },
  ];

  // 25/02/2026 — Extensão pós-adjudicação (b3 do redesign do funil). Só é
  // adicionada quando o lead já tem ``onboarding_stage`` preenchido — i.e.
  // após a aceitação. Torna visível dentro do detalhe do lead a fase actual
  // do onboarding (caução, PI, arranque) sem sobrecarregar o funil comum.
  const onboardingLabels = {
    pendente_caucao: "Pendente Caução",
    caucao_paga_recrutamento: "Caução paga · Recrutamento",
    caucao_paga_total: "Caução Paga Total",
    pi_validado: "PI Validado por DT",
    servico_iniciado: "Serviço Iniciado",
  };
  if (lead.onboarding_stage && onboardingLabels[lead.onboarding_stage]) {
    phases.push({
      key: "onboarding",
      label: onboardingLabels[lead.onboarding_stage],
      at: lead.updated_at,
      done: true,
      terminal: lead.onboarding_stage === "servico_iniciado",
      variant: lead.onboarding_stage === "servico_iniciado" ? "success" : "info",
    });
  }

  // Detect stale current phase: no advance in > 3 days
  const currentIdx = phases.map(p => p.done).lastIndexOf(true);
  const currentPhase = phases[currentIdx];
  const staleDays = currentPhase ? daysSince(currentPhase.at) : null;
  const isStale = !phases[phases.length - 1].done && staleDays !== null && staleDays >= 3;

  return (
    <div className="bg-card border border-border rounded-2xl p-4" data-testid="lead-timeline">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">Percurso do lead</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">Fases concluídas com data e responsável.</div>
        </div>
        {isStale && (
          <div className="flex items-center gap-2 text-xs text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/25 rounded-full px-3 py-1" data-testid="stale-warning">
            <AlertCircle className="w-3.5 h-3.5" />
            Parado há {staleDays} dias
          </div>
        )}
      </div>

      <div className="relative overflow-x-auto">
        <div className="flex items-start gap-1 min-w-full">
          {phases.map((p, idx) => {
            const isLast = idx === phases.length - 1;
            const iconBg = p.done
              ? (p.variant === "danger" ? "bg-rose-500 text-primary-foreground border-rose-500"
                : p.variant === "pending" ? "bg-amber-500 text-primary-foreground border-amber-500"
                : "bg-foreground text-background border-primary")
              : "bg-muted text-muted-foreground/70 border-border";
            const lineBg = phases[idx + 1]?.done ? "bg-foreground" : "bg-muted";
            return (
              <div key={p.key} className="flex-1 min-w-[110px]">
                <div className="flex items-center">
                  <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                    {p.done ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : <Clock className="w-3 h-3" strokeWidth={2} />}
                  </div>
                  {!isLast && <div className={`flex-1 h-0.5 ${lineBg}`}></div>}
                </div>
                <div className="mt-2 pr-2">
                  <div className={`text-[11px] font-semibold ${p.done ? "text-foreground" : "text-muted-foreground"}`}>{p.label}</div>
                  {p.done && p.at && <div className="text-[10px] text-muted-foreground mt-0.5">{fmtDate(p.at)}</div>}
                  {p.done && p.by && <div className="text-[10px] text-muted-foreground mt-0.5">{p.by}</div>}
                  {p.done && p.subtitle && <div className="text-[10px] text-muted-foreground mt-0.5">{p.subtitle}</div>}
                  {p.done && p.reason && <div className="text-[10px] text-[hsl(var(--warning))] mt-0.5 line-clamp-2" title={p.reason}>Motivo: {p.reason}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {changes.length > 0 && (
        <div className="mt-5 pt-4 border-t border-border" data-testid="teams-changes-block">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-3">
            Reunião Teams · alterações detectadas
          </div>
          <ul className="space-y-2">
            {changes.map((c) => {
              const meta = TEAMS_CHANGE_META[c.kind] || TEAMS_CHANGE_META.rescheduled;
              const Icon = meta.icon;
              return (
                <li
                  key={c.id}
                  data-testid={`teams-change-${c.kind}-${c.id}`}
                  className={`flex items-start gap-3 rounded-lg border border-border border-l-4 ${meta.borderL} bg-card p-3`}
                >
                  <div className={`w-8 h-8 rounded-md ${meta.iconBg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-4 h-4 ${meta.iconColor}`} strokeWidth={1.8} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-semibold ${meta.titleColor}`}>{meta.label}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Detectado {fmtDate(c.detected_at)}
                      {c.manager_name ? ` · ${c.manager_name}` : ""}
                    </div>
                    {c.kind === "rescheduled" && (
                      <div className="text-[11px] text-muted-foreground mt-1 tabular-nums">
                        <span className="text-muted-foreground">Antes:</span> {fmtDate(c.previous_scheduled_at)}
                        <span className="mx-1.5 text-muted-foreground/70">→</span>
                        <span className="text-foreground font-medium">Agora: {fmtDate(c.new_scheduled_at)}</span>
                      </div>
                    )}
                    {c.kind !== "rescheduled" && c.previous_scheduled_at && (
                      <div className="text-[11px] text-muted-foreground mt-1">
                        Estava agendada para {fmtDate(c.previous_scheduled_at)}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}



      {supersededVersions.length > 0 && (
        <div className="mt-5 pt-4 border-t border-border" data-testid="proposal-version-history">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">
            Histórico de versões da proposta
          </div>
          <div className="flex flex-wrap gap-2">
            {supersededVersions.map((v) => (
              <span
                key={`superseded-${v.version}`}
                data-testid={`proposal-version-superseded-${v.version}`}
                className="inline-flex items-center gap-1.5 text-[11px] bg-muted border border-border text-foreground rounded-full px-2.5 py-1"
                title={v.replaced_by_name ? `Substituída por ${v.replaced_by_name}` : undefined}
              >
                <span className="font-semibold">{v.version}</span>
                <span className="text-muted-foreground">substituída em {fmtDate(v.replaced_at)}</span>
                {v.replaced_by_version && (
                  <span className="text-muted-foreground">→ {v.replaced_by_version}</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
